import React from 'react';
import { Bell, CalendarClock, Car, ChevronRight, FileText, Plus, QrCode, ShieldCheck, Clock } from 'lucide-react';
import { DmpLogo } from '../../brand/DmpLogo';
import { StatusBadge } from '../../common/StatusBadge';
import { OwnerBottomNav } from '../OwnerBottomNav';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import { useMyVehicles } from '../../../hooks/useMyVehicles';
import { ownerProfile } from '../../../data/ownerProfile';
import { ownerVehicleStatus, profileTone } from '../../../utils/ownerStatus';
import { toBn } from '../../../utils/bn';
import type { OwnerNav, OwnerScreen } from '../../../types/owner';
export function OwnerHome({
  nav
}: {
  nav: OwnerNav;
}) {
  const {
    notifications,
    myProfileStatus
  } = useOwnerService();
  const vehicles = useMyVehicles();
  const unread = notifications.filter(n => !n.read).length;
  const verified = vehicles.filter(v => ownerVehicleStatus(v.app.status).tone === 'green').length;
  const pending = vehicles.length - verified;
  const pad = (n: number) => toBn(String(n).padStart(2, '0'));
  const summary = [{
    label: 'আমার গাড়ি',
    value: pad(vehicles.length),
    cls: 'bg-navy-50 text-navy-800',
    to: 'vehicles' as OwnerScreen
  }, {
    label: 'যাচাইকৃত গাড়ি',
    value: pad(verified),
    cls: 'bg-emerald-50 text-emerald-700',
    to: 'vehicles' as OwnerScreen
  }, {
    label: 'যাচাই অপেক্ষমাণ',
    value: pad(pending),
    cls: 'bg-amber-50 text-amber-700',
    to: 'tracking' as OwnerScreen
  }, {
    label: 'নতুন নোটিফিকেশন',
    value: pad(unread),
    cls: 'bg-blue-50 text-blue-700',
    to: 'notifications' as OwnerScreen
  }];
  const actions: {
    label: string;
    icon: typeof Car;
    to: OwnerScreen;
    tone: string;
    prep?: () => void;
  }[] = [{
    label: 'আমার গাড়ি',
    icon: Car,
    to: 'vehicles',
    tone: 'bg-navy-50 text-navy-800'
  }, {
    label: 'আবেদনের অবস্থা',
    icon: FileText,
    to: 'tracking',
    tone: 'bg-blue-50 text-blue-700'
  }, {
    label: 'ডকুমেন্টের মেয়াদ',
    icon: CalendarClock,
    to: 'expiry',
    tone: 'bg-amber-50 text-amber-700'
  }, {
    label: 'QR স্টিকার',
    icon: QrCode,
    to: 'qr',
    tone: 'bg-emerald-50 text-emerald-700',
    prep: () => nav.setVehicleId('c1')
  }, {
    label: 'নোটিফিকেশন',
    icon: Bell,
    to: 'notifications',
    tone: 'bg-red-50 text-red-700'
  }, {
    label: 'সহায়তা',
    icon: ShieldCheck,
    to: 'support',
    tone: 'bg-slate-100 text-slate-700'
  }];
  return <div className="flex h-full flex-col bg-slate-50" data-id="element-1624" data-mp="5qtjstfgrc37">
      <header className="bg-navy-800 px-4 pb-14 pt-2 text-white" data-id="element-1625" data-mp="jj9rw8n820az">
        <div className="flex items-center gap-2.5" data-id="element-1626" data-mp="jlnown1gpml6">
          <DmpLogo size={34} data-id="element-1627" data-mp="fh67guikrldk" />
          <div className="flex-1" data-id="element-1628" data-mp="fkagc56zj5ak">
            <h1 className="font-heading text-base font-semibold" data-id="element-1629" data-mp="thqnjix3v33d">আমার ড্যাশবোর্ড</h1>
            <p className="text-[11px] text-navy-200" data-id="element-1630" data-mp="oiw72nmm1qmf">ডিএমপি গাড়ির মালিক সেবা</p>
          </div>
          <button onClick={() => nav.go('notifications')} aria-label="নোটিফিকেশন" className="relative flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/10" data-id="element-1631" data-mp="2v9c4xyraaea3">
            <Bell className="h-5 w-5" data-id="element-1632" data-mp="2qc3ny1hvggaf" />
            {unread > 0 && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-flagred" data-id="element-1633" data-mp="15ryhinxmum1h" />}
          </button>
        </div>
      </header>

      <div className="no-scrollbar -mt-10 min-h-0 flex-1 overflow-y-auto px-4 pb-4" data-id="element-1634" data-mp="1aqjgyl5291ov">
        <button onClick={() => nav.go('account')} className="flex w-full items-center gap-3 rounded-xl border border-border bg-white p-3.5 text-left shadow-sm" data-id="element-1635" data-mp="w1vpor28ze4q">
          <img src={ownerProfile.photo} alt="" className="h-12 w-12 rounded-full object-cover" data-id="element-1636" data-mp="n17c1xgrnfsd" />
          <div className="min-w-0 flex-1" data-id="element-1637" data-mp="s1cucn15bpxs">
            <p className="text-[11px] text-slate-500" data-id="element-1638" data-mp="s2ei9s39g6wx">স্বাগতম,</p>
            <p className="truncate font-heading text-base font-bold text-navy-900" data-id="element-1639" data-mp="d4cdxfoenhem">{ownerProfile.name}</p>
            <StatusBadge tone={profileTone(myProfileStatus)} className="mt-0.5 !text-[10.5px]" data-id="element-1640" data-mp="i3oz7vzmogf3">
              {myProfileStatus === 'যাচাইকৃত' ? <ShieldCheck className="h-3 w-3" data-id="element-1641" data-mp="27idaftwhfsbj" /> : <Clock className="h-3 w-3" data-id="element-1642" data-mp="2cgetietwrrn0" />}প্রোফাইল {myProfileStatus}
            </StatusBadge>
          </div>
          <ChevronRight className="h-4 w-4 text-slate-400" data-id="element-1643" data-mp="cexf420klmuc" />
        </button>

        <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-1644" data-mp="8cu238y96a6y">
          {summary.map(s => <button key={s.label} onClick={() => nav.go(s.to)} className="rounded-xl border border-border bg-white p-3 text-left" data-id="element-1645" data-mp="8azflq7c2a0r">
              <p className="text-[11.5px] text-slate-500" data-id="element-1646" data-mp="3ukjuqiduxkyr">{s.label}</p>
              <p className={`mt-1 inline-block rounded px-1.5 font-heading text-2xl font-bold ${s.cls}`} data-id="element-1647" data-mp="3umefnwp4pd2u">{s.value}</p>
            </button>)}
        </div>

        <button onClick={() => nav.go('addVehicle')} className="mt-3 flex w-full items-center gap-3 rounded-xl bg-bdgreen-600 p-4 text-left text-white shadow-sm active:scale-[0.99]" data-id="element-1648" data-mp="3ksnw967uzsu0">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/15" data-id="element-1649" data-mp="3buln23dz1tvn"><Plus className="h-6 w-6" data-id="element-1650" data-mp="36uzdq519kxs4" /></span>
          <span className="flex-1" data-id="element-1651" data-mp="36to6fv8m17w9">
            <span className="block font-heading text-lg font-bold" data-id="element-1652" data-mp="31vmppgynpkny">নতুন গাড়ি যুক্ত করুন</span>
            <span className="text-xs text-bdgreen-100" data-id="element-1653" data-mp="2rrfzga2b88pb">সিএনজি/অটোরিকশা বা মোটরসাইকেল</span>
          </span>
          <ChevronRight className="h-5 w-5" data-id="element-1654" data-mp="2wq95xij6izfz" />
        </button>

        <div className="mt-3 grid grid-cols-3 gap-2" data-id="element-1655" data-mp="2wslw2iz3cg44">
          {actions.map(a => <button key={a.label} onClick={() => {
          a.prep?.();
          nav.go(a.to);
        }} className="flex flex-col items-center gap-1.5 rounded-xl border border-border bg-white px-1 py-3 text-center" data-id="element-1656" data-mp="31qdmt9o01w6q">
              <span className={`flex h-10 w-10 items-center justify-center rounded-full ${a.tone}`} data-id="element-1657" data-mp="292husi9dksga"><a.icon className="h-5 w-5" data-id="element-1658" data-mp="2e0t32ok0uuwe" /></span>
              <span className="text-[11.5px] font-medium leading-tight text-navy-900" data-id="element-1659" data-mp="2iz4c4x2ht1dk">{a.label}</span>
            </button>)}
        </div>

        <div className="mb-2 mt-5 flex items-center justify-between" data-id="element-1660" data-mp="2nqmenpk5za0n">
          <h2 className="text-sm font-semibold text-navy-900" data-id="element-1661" data-mp="1qayr097j1ad7">আমার গাড়িসমূহ</h2>
          <button onClick={() => nav.go('vehicles')} className="text-xs font-medium text-navy-600" data-id="element-1662" data-mp="1v9jqu7iv9s14">সব দেখুন</button>
        </div>
        <div className="space-y-2" data-id="element-1663" data-mp="1n82c8qgot8gj">
          {vehicles.map(({
          vehicle: v,
          app
        }) => {
          const st = ownerVehicleStatus(app.status);
          return <button key={v.id} onClick={() => {
            nav.setVehicleId(v.id);
            nav.setDetailTab('info');
            nav.go('vehicleDetail');
          }} className="flex w-full items-center gap-3 rounded-xl border border-border bg-white p-2.5 text-left" data-id="element-1664" data-mp="1wynjt3ec9nbl">
                <img src={v.image} alt="" className="h-14 w-16 rounded-lg object-cover" data-id="element-1665" data-mp="1496lxq7y9e7z" />
                <div className="min-w-0 flex-1" data-id="element-1666" data-mp="zbeqwnc0dbzk">
                  <p className="truncate text-sm font-semibold text-navy-900" data-id="element-1667" data-mp="zcpvgpda2nrq">{v.reg}</p>
                  <p className="text-[11px] text-slate-500" data-id="element-1668" data-mp="1e9qp4yf14ssu">{v.typeLabel} • {v.brand} {v.model}</p>
                </div>
                <StatusBadge tone={st.tone} className="!text-[10.5px]" data-id="element-1669" data-mp="19b5lsq4hdt06">{st.label}</StatusBadge>
              </button>;
        })}
        </div>
        <p className="mt-4 text-center text-[10.5px] text-slate-400" data-id="element-1670" data-mp="2kn6iqsbi0nr0">প্রোটোটাইপ • প্রদর্শিত সকল তথ্য নমুনা</p>
      </div>
      <OwnerBottomNav nav={nav} data-id="element-1671" data-mp="2s661qwv1sd9u" />
    </div>;
}